import React, { useState } from 'react';
import '../../styles/plan-review.css';
import { LearningScene } from './learningAssets';

/*
 * PlanReview — the agent PROPOSES, the learner disposes. Shows the planner's
 * rationale (bulleted reasons), a tabular "progress & what's next" view, and a
 * natural-language Adjust box that sends feedback back to the planner for a
 * revised plan. Content materializes in the background while this is on screen,
 * so Start is instant.
 */
const STATUS = {
  new: { label: 'New', color: 'var(--n-info)', bg: 'var(--n-info-soft)' },
  weak: { label: 'Weak', color: 'var(--n-err-ink)', bg: 'var(--n-err-soft)' },
  stale: { label: 'Stale', color: 'var(--n-warn-ink)', bg: 'var(--n-warn-soft)' },
  shaky: { label: 'Shaky', color: 'var(--n-warn)', bg: 'var(--n-warn-soft)' },
  strong: { label: 'Strong', color: 'var(--n-ok-ink)', bg: 'var(--n-ok-soft)' },
};
const IMPORTANCE = { must_know: 'Must-know', core: 'Core', advanced: 'Advanced' };
const masteryColor = (m) => (m >= 0.7 ? '#10b981' : m >= 0.4 ? '#f59e0b' : '#ef4444');
const tail = (id) => String(id || '').split('.').pop();

const Chip = ({ s }) => {
  const c = STATUS[s] || STATUS.new;
  return <span className="pr-chip" style={{ color: c.color, background: c.bg }}>{c.label}</span>;
};

const PlanReview = ({ plan, subjects = [], isActive, materializing, contentReady, onStart, onAdjust, onBack }) => {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [revising, setRevising] = useState(false);
  const [adjErr, setAdjErr] = useState('');

  if (!plan) return null;
  const focus = plan.focus_areas || [];
  const focusIds = new Set(focus.map((f) => f.concept_id));
  const rest = (plan.progress || []).filter((p) => !focusIds.has(p.concept_id)).slice(0, 8);
  const alloc = plan.allocation || {};
  const allocText = Object.entries(alloc).filter(([, v]) => v > 0)
    .map(([m, v]) => `${m} ×${v}`).join(' · ');
  const progressById = {};
  (plan.progress || []).forEach((p) => { progressById[p.concept_id] = p; });

  const sendFeedback = async () => {
    const fb = feedback.trim();
    if (!fb || revising) return;
    setRevising(true); setAdjErr('');
    try {
      await onAdjust(fb);
      setFeedback(''); setAdjustOpen(false);
    } catch (e) {
      setAdjErr(e.message || 'The agent could not revise the plan.');
    } finally {
      setRevising(false);
    }
  };

  const levelCell = (p) => {
    if (!p || p.status === 'new') {
      return <span className="pr-imp">{IMPORTANCE[p && p.importance] || 'Not started'}</span>;
    }
    return (
      <div className="pr-level">
        <div className="pr-bar"><span style={{ width: `${Math.round(p.mastery * 100)}%`, background: masteryColor(p.mastery) }} /></div>
        <span className="pr-pct" style={{ color: masteryColor(p.mastery) }}>{Math.round(p.mastery * 100)}%</span>
      </div>
    );
  };

  return (
    <div className="pr-wrap">
      <div className="pr-head pr-head--story">
        <div className="pr-head-copy">
          <div className="pr-kicker"><LearningScene name="theory" size={15} /> Agent rationale</div>
          <div className="pr-title">Your practice plan</div>
          <div className="pr-sub">
            Planned by your agent from {subjects.length} topic{subjects.length === 1 ? '' : 's'} and your memory
            {plan.cold_start ? ' — first session on these topics' : ''}{isActive ? ' — this session is in progress' : ''}.
            {plan.generated_by === 'fallback' && <span className="pr-fallback"> (heuristic — the reasoning model was unavailable)</span>}
          </div>
        </div>
        <div className="pr-head-art" aria-hidden="true"><LearningScene name="theory" size={74} /></div>
        <button className="pr-ghost" type="button" onClick={onBack}>‹ Change topics</button>
      </div>

      <div className="pr-grid">
        <div className="pr-card pr-why">
          <div className="pr-card-h">Why this plan</div>
          <ul className="pr-reasons">
            {(plan.rationale || []).map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          {(plan.deltas || []).length > 0 && (
            <div className="pr-deltas">
              <div className="pr-deltas-h">Since your last session</div>
              {plan.deltas.map((d, i) => <span className="pr-delta" key={i}>{d}</span>)}
            </div>
          )}
          {allocText && <div className="pr-alloc">This session: <b>{allocText}</b></div>}
        </div>

        <div className="pr-card pr-tablecard">
          <div className="pr-card-h">Progress &amp; what to expect next</div>
          <div className="pr-tablewrap">
            <table className="pr-table">
              <thead>
                <tr><th>Area</th><th>Your level</th><th>Status</th><th>Coming up</th><th>Why</th></tr>
              </thead>
              <tbody>
                {focus.map((f) => {
                  const p = progressById[f.concept_id] || f;
                  return (
                    <tr key={f.concept_id} className="pr-row-focus">
                      <td>
                        <div className="pr-area">{f.name || tail(f.concept_id)}</div>
                        <div className="pr-areaid">{f.concept_id}</div>
                      </td>
                      <td>{levelCell(p)}</td>
                      <td><Chip s={f.status} /></td>
                      <td>
                        <div className="pr-coming">
                          {(f.modes || []).map((m) => <span className="pr-mode" key={m}>{m}</span>)}
                          {f.difficulty && <span className="pr-diff">{f.difficulty}</span>}
                          {f.start_depth > 1 && <span className="pr-depth">depth {f.start_depth}</span>}
                        </div>
                      </td>
                      <td className="pr-why-cell">{f.reason}</td>
                    </tr>
                  );
                })}
                {rest.map((p) => (
                  <tr key={p.concept_id} className="pr-row-rest">
                    <td>
                      <div className="pr-area">{p.name || tail(p.concept_id)}</div>
                      <div className="pr-areaid">{p.concept_id}</div>
                    </td>
                    <td>{levelCell(p)}</td>
                    <td><Chip s={p.status} /></td>
                    <td><span className="pr-later">later</span></td>
                    <td className="pr-why-cell">In scope — the agent will rotate it in as you progress.</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="pr-foot">
        <div className={`pr-mat${contentReady ? ' ready' : ''}`}>
          {contentReady
            ? <>✓ Questions ready — you'll start instantly</>
            : materializing
              ? <><span className="pr-spin" /> Preparing your questions in the background…</>
              : null}
        </div>
        <div className="pr-actions">
          <button className="pr-secondary" type="button" onClick={() => setAdjustOpen((v) => !v)}>
            Adjust plan
          </button>
          <button className="pr-primary" type="button" onClick={onStart}>{isActive ? 'Continue session →' : 'Start session →'}</button>
        </div>
      </div>

      {adjustOpen && (
        <div className="pr-adjust">
          <div className="pr-adjust-h">Tell the agent what to change</div>
          <textarea
            className="pr-adjust-in"
            placeholder='e.g. "More RAG, skip coding" · "Make it harder" · "Focus only on my weak spots"'
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={revising}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendFeedback(); }}
          />
          <div className="pr-adjust-row">
            {adjErr && <span className="pr-adjust-err">{adjErr}</span>}
            <span className="pr-spacer" />
            <button className="pr-secondary" type="button" onClick={() => setAdjustOpen(false)} disabled={revising}>Cancel</button>
            <button className="pr-primary" type="button" onClick={sendFeedback} disabled={revising || !feedback.trim()}>
              {revising ? 'Revising…' : 'Revise plan'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanReview;
